import {ReactElement, useCallback, useContext, useState} from "react";
import {Button} from "@zendeskgarden/react-buttons";
import {csvBoxExtractRows, csvBoxGeneralError, csvBoxHasError, normalizeDate} from "../../../../global/csv_box_helpers";
import {AuthContext} from "../../../../contexts/AuthContext";
import {
    PayrollBonusFrequency,
    PayrollBonusType,
    PayrollCompensationType,
    PayrollEmployeeClassification,
    PayrollRaiseFrequency,
    PayrollRaiseType,
    useGeneratePayrollEmployeesFileUploadUrlMutation,
    useUploadPayrollEmployeesFileMutation
} from "../../../../__generated__/generated_types";
import {CSVBoxButton} from "../../../../components/csvbox/CSVBoxButton";
import {toast} from "react-toastify";

type Optional<T> = T | null | undefined;

type OptionalString = string | null | undefined;

interface PayrollEmployeeData {
    line: number;
    firstName: string;
    lastName: OptionalString;
    employeeId: string;
    startDate: string;
    classification: PayrollEmployeeClassification;
    position: string;
    propertyName: string;

    baseCompType: PayrollCompensationType;
    baseCompAnnual: OptionalString;
    baseCompHourlyRate: OptionalString;
    baseCompHours: OptionalString;

    overtimeHourlyRate: OptionalString;
    overtimeHours: OptionalString;
    overtimeStartDate: OptionalString;
    overtimeEndDate: OptionalString;

    raiseFreq: Optional<PayrollRaiseFrequency>;
    raiseType: Optional<PayrollRaiseType>;
    raisePercent: OptionalString;
    raiseAmount: OptionalString;
    raiseStartDate: OptionalString;
    raiseEndDate: OptionalString;

    bonusFreq: Optional<PayrollBonusFrequency>;
    bonusType: Optional<PayrollBonusType>;
    bonusPercent: OptionalString;
    bonusAmount: OptionalString;
    bonusStartDate: OptionalString;
    bonusEndDate: OptionalString;

    medicalCostPerPeriod: OptionalString;
    medicalEffectiveDate: OptionalString;

    dentalCostPerPeriod: OptionalString;
    dentalEffectiveDate: OptionalString;

    visionCostPerPeriod: OptionalString;
    visionEffectiveDate: OptionalString;

    socialSecurityPercentage: OptionalString;
    socialSecurityCap: OptionalString;
    socialSecurityEffectiveDate: OptionalString;

    futaPercentage: OptionalString;
    futaCap: OptionalString;
    futaEffectiveDate: OptionalString;

    sutaPercentage: OptionalString;
    sutaCap: OptionalString;
    sutaEffectiveDate: OptionalString;

    medicarePercentage: OptionalString;
    medicareCap: OptionalString;
    medicareEffectiveDate: OptionalString;

    workersCompPercentage: OptionalString;
    workersCompCap: OptionalString;
    workersCompEffectiveDate: OptionalString;

    _401kMatchPercentage: OptionalString;
    _401kMatchCap: OptionalString;
    _401kMatchEffectiveDate: OptionalString;
}

function extractData(values: unknown[]): PayrollEmployeeData[] {
    let line = 0;
    const retVals: PayrollEmployeeData[] = [];
    for(const value of values) {
        if(typeof value !== "object" || value === null) {
            continue;
        }

        const row = value as Record<string, unknown>;

        // Required values. If any is missing/invalid, skip the entire row.
        const firstName = optionalString(row["First Name"]);
        const employeeId = optionalString(row["Employee ID"]);
        const startDate = normalizeDate(optionalString(row["Start Date"]));
        const position = optionalString(row["Position"]);
        const propertyName = optionalString(row["Property Name"]);
        const classification = mapClassification(row["Classification"]);
        const baseCompType = mapCompensationType(row["Base Compensation Type"]);

        if(
                firstName === null || firstName === undefined ||
                employeeId === null || employeeId === undefined ||
                startDate === null || startDate === undefined ||
                position === null || position === undefined ||
                propertyName === null || propertyName === undefined ||
                classification === undefined ||
                baseCompType === undefined
        ) {
            continue;
        }

        const normalizeDateIfDefined = (value: OptionalString): OptionalString => {
            return value !== undefined && value !== null ? normalizeDate(value) : undefined;
        };

        retVals.push({
            line: line,
            firstName: firstName,
            lastName: optionalString(row["Last Name"]),
            employeeId: employeeId,
            startDate: startDate,
            classification: classification,
            position: position,
            propertyName: propertyName,

            baseCompType: baseCompType,
            baseCompAnnual: optionalString(row["Base Compensation Annual"]),
            baseCompHourlyRate: optionalString(row["Base Compensation Hourly Rate"]),
            baseCompHours: optionalString(row["Base Compensation Hours/Week"]),

            overtimeHourlyRate: optionalString(row["Overtime Hourly Rate"]),
            overtimeHours: optionalString(row["Overtime Hours/Week"]),
            overtimeStartDate: normalizeDateIfDefined(optionalString(row["Overtime Start Date"])),
            overtimeEndDate: normalizeDateIfDefined(optionalString(row["Overtime End Date"])),

            raiseFreq: mapRaiseFrequency(row["Raise Once/Recurring"]),
            raiseType: mapRaiseType(row["Raise Type"]),
            raisePercent: optionalString(row["Raise Percent of Pay"]),
            raiseAmount: optionalString(row["Raise Fixed Amount"]),
            raiseStartDate: normalizeDateIfDefined(optionalString(row["Raise Effective Date"])),
            raiseEndDate: undefined,

            bonusFreq: mapBonusFrequency(row["Bonus Once/Recurring"]),
            bonusType: mapBonusType(row["Bonus Type"]),
            bonusPercent: optionalString(row["Bonus Percent of Pay"]),
            bonusAmount: optionalString(row["Bonus Fixed Amount"]),
            bonusStartDate: normalizeDateIfDefined(optionalString(row["Bonus Effective Date"])),
            bonusEndDate: undefined,

            medicalCostPerPeriod: optionalString(row["Medical Cost Per Pay Period"]),
            medicalEffectiveDate: normalizeDateIfDefined(optionalString(row["Medical Effective Date"])),

            dentalCostPerPeriod: optionalString(row["Dental Cost Per Pay Period"]),
            dentalEffectiveDate: normalizeDateIfDefined(optionalString(row["Dental Effective Date"])),

            visionCostPerPeriod: optionalString(row["Vision Cost Per Pay Period"]),
            visionEffectiveDate: normalizeDateIfDefined(optionalString(row["Vision Effective Date"])),

            socialSecurityPercentage: optionalString(row["Social Security % of Salary"]),
            socialSecurityCap: optionalString(row["Social Security Cap"]),
            socialSecurityEffectiveDate: normalizeDateIfDefined(optionalString(row["Social Security Effective Date"])),

            futaPercentage: optionalString(row["Federal Unemployment Tax % of Salary"]),
            futaCap: optionalString(row["Federal Unemployment Tax Cap"]),
            futaEffectiveDate: normalizeDateIfDefined(optionalString(row["Federal Unemployment Tax Effective Date"])),

            sutaPercentage: optionalString(row["State Unemployment Tax % of Salary"]),
            sutaCap: optionalString(row["State Unemployment Tax Cap"]),
            sutaEffectiveDate: normalizeDateIfDefined(optionalString(row["State Unemployment Tax Effective Date"])),

            medicarePercentage: optionalString(row["Medicare % of Salary"]),
            medicareCap: optionalString(row["Medicare Cap"]),
            medicareEffectiveDate: normalizeDateIfDefined(optionalString(row["Medicare Effective Date"])),

            workersCompPercentage: optionalString(row["Workers Comp % of Salary"]),
            workersCompCap: optionalString(row["Workers Comp Cap"]),
            workersCompEffectiveDate: normalizeDateIfDefined(optionalString(row["Workers Comp Effective Date"])),

            _401kMatchPercentage: optionalString(row["401k Match % of Salary"]),
            _401kMatchCap: optionalString(row["401k Match Cap"]),
            _401kMatchEffectiveDate: normalizeDateIfDefined(optionalString(row["401k Match Effective Date"])),
        });
        line++;
    }

    return retVals;
}

function optionalString(value: unknown): OptionalString {
    if(typeof value !== "string") {
        return undefined;
    }
    const trimmed = value.trim();
    return trimmed === "" ? undefined : trimmed;
}

function mapClassification(value: unknown): PayrollEmployeeClassification | undefined {
    switch(optionalString(value)?.toUpperCase()) {
        case "CONTRACTOR":
            return PayrollEmployeeClassification.Contractor;
        case "EMPLOYEE":
            return PayrollEmployeeClassification.Employee;
        default:
            return undefined;
    }
}

function mapCompensationType(value: unknown): PayrollCompensationType | undefined {
    switch(optionalString(value)?.toUpperCase()) {
        case "ANNUAL":
            return PayrollCompensationType.Annual;
        case "HOURLY":
            return PayrollCompensationType.Hourly;
        default:
            return undefined;
    }
}

function mapRaiseFrequency(value: unknown): Optional<PayrollRaiseFrequency> {
    switch(optionalString(value)?.toUpperCase()) {
        case "ONE_TIME":
            return PayrollRaiseFrequency.Once;
        case "ANNUAL_RECURRING":
            return PayrollRaiseFrequency.Annually;
        default:
            return undefined;
    }
}

function mapRaiseType(value: unknown): Optional<PayrollRaiseType> {
    switch(optionalString(value)?.toUpperCase()) {
        case "FIXED_AMOUNT":
            return PayrollRaiseType.FixedAmount;
        case "PERCENT_OF_SALARY":
            return PayrollRaiseType.PercentOfSalary;
        default:
            return undefined;
    }
}

function mapBonusFrequency(value: unknown): Optional<PayrollBonusFrequency> {
    switch(optionalString(value)?.toUpperCase()) {
        case "ONE_TIME":
            return PayrollBonusFrequency.Once;
        case "ANNUAL_RECURRING":
            return PayrollBonusFrequency.Annually;
        default:
            return undefined;
    }
}

function mapBonusType(value: unknown): Optional<PayrollBonusType> {
    switch(optionalString(value)?.toUpperCase()) {
        case "FIXED_AMOUNT":
            return PayrollBonusType.FixedAmount;
        case "PERCENT_OF_SALARY":
            return PayrollBonusType.PercentOfSalary;
        default:
            return undefined;
    }
}

export interface PayrollEmployeeUploadButtonProps {
    budgetYear: number;
    jobCreated: (jobId: string) => void;
    buttonClassName: string;
}

export function PayrollEmployeeUploadButton(props: PayrollEmployeeUploadButtonProps): ReactElement {
    const {user} = useContext(AuthContext);


    const [generateUrl] = useGeneratePayrollEmployeesFileUploadUrlMutation({
        notifyOnNetworkStatusChange: true,
    });

    const [uploadFile] = useUploadPayrollEmployeesFileMutation({
        notifyOnNetworkStatusChange: true,
    });

    const [saving, setSaving] = useState<boolean>(false);

    function handleSave(data: unknown): void {
        setSaving(true);
        const rows = csvBoxExtractRows(data);
        if(rows === undefined) {
            csvBoxGeneralError("employee upload");
            setSaving(false);
            return;
        }

        const extracted = extractData(rows);
        if(extracted.length === 0) {
            csvBoxGeneralError("employee upload");
            setSaving(false);
            return;
        }

        generateUrl()
            .then(res => {
                if(res.data?.generatePayrollEmployeesFileUploadUrl) {
                    const urlData = res.data.generatePayrollEmployeesFileUploadUrl;
                    return JSON.parse(urlData) as {key: string, signedUrl: string};
                }
                throw new Error("url");
            }).catch(() => {
                throw new Error("url");
            }).then(urlData => {
                const encoder = new TextEncoder();
                const encoded = encoder.encode(JSON.stringify(extracted));

                const res = fetch(urlData.signedUrl, {
                    method: "PUT",
                    body: encoded.buffer,
                    headers: {
                        "Content-Type": "application/json",
                    },
                });
                return Promise.all([Promise.resolve(urlData.key), res]);
            }).then(res => {
                const [key, resp] = res;
                if(resp.ok) {
                    return uploadFile({
                        variables: {
                            fileKey: key,
                            budgetYear: props.budgetYear
                        }
                    });
                }
                throw new Error("upload");
            }).then(uploadResult => {
                if(uploadResult.data?.uploadPayrollEmployeesFile) {
                    props.jobCreated(uploadResult.data.uploadPayrollEmployeesFile.id);
                    toast.success("Payroll employees file submitted for processing");
                    return;
                }
                throw new Error("upload");
            }).catch(err => {
                if(err.message === "url") {
                    toast.error("Error generating upload URL");
                } else if(err.message === "upload") {
                    toast.error("Error uploading file");
                } else {
                    toast.error("Error uploading file");
                    console.error(err);
                }
            }).finally(() => {
                setSaving(false);
            });
    }

    const importCallback = useCallback((result: boolean, data: any) => {
        if(!csvBoxHasError(result, data)) {
            handleSave(data);
        } else {
            csvBoxGeneralError("account values");
        }
    }, []);

    const renderCallback = useCallback((launch: any, isLoading: boolean) => {
        return <Button className={props.buttonClassName} onClick={launch} disabled={isLoading || saving}>Upload Employees</Button>;
    }, [props.buttonClassName, saving]);

    return <CSVBoxButton
            licenseKey="tdItNANSagCHSn5FHpBd0pTjeVcLNT"
            user={{
                userId: user?.id ?? "unknown",
                name: user?.firstName || user?.lastName ? `${user?.firstName || ""} ${user?.lastName || ""}` : "unknown",
                email: user?.email ?? "unknown",
                clientId: user?.clientId ?? "unknown",
                clientName: user?.clientName ?? "unknown",
            }}
            environment={{
                // This gets set based on the environment to facilitate validation callbacks.
                "base_url": process.env["REACT_APP_CSV_BOX_BASE_URL"],
                "budget_year": props.budgetYear
            }}
            onImport={importCallback}
            render={renderCallback}
    />;
}